import type { ReactNode } from "react";
import { Loader } from "@/components/common/loader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

interface TabShellProps {
  /** Card title. Omit when a page-level header already provides it (e.g. the
   * standalone Practitioners page), so it isn't shown twice. */
  title?: string;
  description?: string;
  loading?: boolean;
  error?: boolean;
  children: ReactNode;
  /** Optional element rendered in the header's top-right (e.g. an action button). */
  action?: ReactNode;
}

/**
 * Shared card frame for every settings tab — keeps the title/description,
 * loading spinner and error message consistent across tabs instead of each
 * one re-implementing them.
 */
export function TabShell({ title, description, loading, error, children, action }: TabShellProps) {
  const hasHeaderText = !!title || !!description;
  return (
    <Card>
      {(hasHeaderText || action) && (
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          {hasHeaderText ? (
            <div>
              {title && <CardTitle>{title}</CardTitle>}
              {description && <CardDescription>{description}</CardDescription>}
            </div>
          ) : (
            <div />
          )}
          {action}
        </CardHeader>
      )}
      <CardContent>
        {error ? (
          <p className="text-destructive text-sm">
            Couldn&apos;t load this section. Please refresh and try again.
          </p>
        ) : loading ? (
          <div className="flex justify-center py-12">
            <Loader />
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}
